import React, { useEffect, useMemo, useRef, useState } from "react";
import AAC from "../Components/Assets/AAC Blocks.jpg";
import Chemical from "../Components/Assets/Chemical.jpg";
import Construction from "../Components/Assets/Construction.jpg";
import GFRP from "../Components/Assets/GFRP.jpg";
import Formwork from "../Components/Assets/Formwork.jpg";
import Light from "../Components/Assets/Light.jpg";
import Safety from "../Components/Assets/Safety.jpg";

import axiosInstance from "./axiosInstance";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/navigation";
import { useNavigate } from "react-router-dom";
import { baseurl } from "../config/url";
import { Helmet } from "react-helmet-async";

const Onexvideo = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [productBox2, setProductBox2] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSubcats, setIsLoadingSubcats] = useState(false);
  const navigate = useNavigate();

  // for users who prefer reduced motion
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // slides with good alt text
  const imageSlides = useMemo(
    () => [
      { src: AAC, alt: "Lightweight AAC blocks 600x200x200 mm with fire resistance, thermal insulation and high compressive strength for modern construction" },
      { src: Chemical, alt: "High-performance construction chemicals including curing compound, admixture, grout, adhesive and waterproofing solutions" },
      { src: Construction, alt: "Durable chemical and mechanical anchors for strong and reliable fastening in concrete structures" },
      { src: GFRP, alt: "Corrosion-resistant GFRP rebar with high tensile strength, long durability and eco-friendly construction performance" },
      { src: Formwork, alt: "Mivan shuttering and cuplock system formwork materials for efficient and safe construction" },
      {src:Light,alt:"Best LED lights for industrial and residential construction – energy-efficient lighting solutions with 80% power saving from top construction material supplier company."},
      {src:Safety,alt:"Construction safety materials including helmets, safety harness, gloves, safety shoes, reflective jackets, ear plugs, safety goggles, fire extinguishers, safety nets, face masks, gumboots, PPE kits and industrial uniforms supplied by trusted construction material supplier"}

    ],
    []
  );

  // fetch helper
  const fetchData = async (url, setState) => {
    try {
      const response = await axiosInstance.get(url);
      setState(response?.data || []);
    } catch (error) {
      console.error(`Error fetching data from ${url}:`, error);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsLoading(true);
      try {
        const cats = await axiosInstance.get(`${baseurl}/api/category/get-all`);
        if (mounted) setCategories(cats?.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setIsLoading(false);
      }
      // load powerpack (not blocking UI)
      fetchData(`${baseurl}/api/seller/products/box2`, setProductBox2);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleCategoryClick = async (categoryId) => {
    if (selectedCategoryId === categoryId) {
      setSelectedCategoryId(null);
      setSubcategories([]);
      return;
    }
    setSelectedCategoryId(categoryId);
    setIsLoadingSubcats(true);
    try {
      const response = await axiosInstance.get(
        `${baseurl}/api/subcategory/category/${categoryId}`
      );
      setSubcategories(response?.data || []);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    } finally {
      setIsLoadingSubcats(false);
    }
  };

  const handleImageClick = (subcategoryId, categoryname) => {
    navigate(`/products/${subcategoryId}`, { state: { categoryname } });
  };

  const handleImageClickPowerPack = (productId) => {
    navigate(`/product/details/${productId}`);
  };

  const formatINR = (n) =>
    typeof n === "number"
      ? new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 0,
        }).format(n)
      : n;

  const pageTitle = "BuildPro-OGCS | Building Supplies at Your Fingertips";
  const pageDescription =
    "Explore construction categories, AAC blocks, GFRP, chemicals, and formwork. View products and quick highlights on BuildPro-OGCS.";

  // --- optional: gentle auto-scroll of category chips L→R ---
  const catTrackRef = useRef(null);
  const pauseRef = useRef(false);
  const userPauseUntilRef = useRef(0);
  useEffect(() => {
    const el = catTrackRef.current;
    if (!el || prefersReducedMotion) return;
    let rafId = 0;
    const SPEED = 0.6;
    const GAP = 16;
    const tick = () => {
      const now = performance.now();
      if (pauseRef.current || now < userPauseUntilRef.current) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll > 0) {
        el.scrollLeft += SPEED;
        if (el.scrollLeft >= maxScroll - 1) el.scrollLeft = GAP;
      }
      rafId = requestAnimationFrame(tick);
    };
    const briefPause = () => (userPauseUntilRef.current = performance.now() + 2500);
    const pause = () => (pauseRef.current = true);
    const resume = () => (pauseRef.current = false);

    el.addEventListener("mouseenter", pause);
    el.addEventListener("mouseleave", resume);
    el.addEventListener("focusin", pause);
    el.addEventListener("focusout", resume);
    el.addEventListener("wheel", briefPause, { passive: true });
    el.addEventListener("touchstart", briefPause, { passive: true });
    el.addEventListener("keydown", briefPause);

    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener("mouseenter", pause);
      el.removeEventListener("mouseleave", resume);
      el.removeEventListener("focusin", pause);
      el.removeEventListener("focusout", resume);
      el.removeEventListener("wheel", briefPause);
      el.removeEventListener("touchstart", briefPause);
      el.removeEventListener("keydown", briefPause);
    };
  }, [prefersReducedMotion, categories.length]);

  return (
    <section className="bg-blue-50 px-3 sm:px-6 pt-8 pb-14" aria-labelledby="homepage-section-title">
      {/* SEO */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link
          rel="canonical"
          href={
            typeof window !== "undefined"
              ? `${window.location.origin}${window.location.pathname}`
              : ""
          }
        />
        <link rel="preload" as="image" href={imageSlides[0].src} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={imageSlides[0].src} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={imageSlides[0].src} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "BuildPro-OGCS Home Highlights",
            description: pageDescription,
            publisher: { "@type": "Organization", name: "OGCS Private Limited" },
            primaryImageOfPage: imageSlides[0].src,
          })}
        </script>
      </Helmet>

      <h2 id="homepage-section-title" className="text-2xl sm:text-3xl font-extrabold text-center tracking-tight text-sky-900">
        Building Supplies at Your Fingertips
      </h2>
       

      {/* Categories (auto-scroll) */}
      <div className="mt-6">
        <div
          ref={catTrackRef}
          className="flex gap-2 overflow-x-auto no-scrollbar py-1 px-1 -mx-1"
          aria-label="Product categories"
        >
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="min-w-[110px] h-9 rounded-full bg-sky-100/70 animate-pulse" aria-hidden="true" />
              ))
            : categories.map((category) => {
                const active = selectedCategoryId === category._id;
                return (
                  <button
                    key={category._id}
                    onClick={() => handleCategoryClick(category._id)}
                    className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sky-400 ${
                      active
                        ? "bg-sky-600 text-white border-sky-600 shadow-sm"
                        : "bg-white text-sky-800 border-sky-200 hover:bg-sky-50"
                    }`}
                    aria-pressed={active}
                  >
                    {category.name}
                  </button>
                );
              })}
        </div>
      </div>

      {/* Subcategories grid */}
      <div className="mt-6">
        {isLoadingSubcats ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-sky-100 p-3">
                <div className="relative w-full overflow-hidden rounded-lg bg-sky-100/60">
                  <div className="pb-[66%] w-full" />
                </div>
                <div className="h-4 bg-sky-100/70 rounded mt-3 w-2/3 animate-pulse" />
              </div>
            ))}
          </div>
        ) : subcategories.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" aria-label="Subcategories grid">
            {subcategories.map((subcategory) => (
              <button
                key={subcategory._id}
                onClick={() => handleImageClick(subcategory._id, subcategory.name)}
                className="group bg-white rounded-xl shadow-sm border border-sky-100 overflow-hidden text-left transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sky-400"
                aria-label={`Open ${subcategory.name} products`}
              >
                <div className="relative w-full overflow-hidden">
                  <div className="pb-[75%] sm:pb-[66%] w-full" />
                  {subcategory.image ? (
                    <img
                      src={`${baseurl}${subcategory.image}`}
                      alt={`${subcategory.name} subcategory`}
                      className="absolute inset-0 w-full h-full object-contain bg-slate-100 img-crisp"
                      loading="lazy"
                      decoding="async"
                      width="320"
                      height="213"
                      sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 16vw"
                      onError={(e) => {
                        e.currentTarget.src =
                          "data:image/svg+xml;charset=UTF-8," +
                          encodeURIComponent(
                            `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='266'><rect width='100%' height='100%' fill='#e2e8f0'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#64748b' font-family='Arial' font-size='14'>Image unavailable</text></svg>`
                          );
                      }}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-sky-100/60 flex items-center justify-center text-sky-400 text-sm">
                      No image
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold text-sky-900 line-clamp-1 min-h-[1.5rem]">
                    {subcategory.name}
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : selectedCategoryId ? (
          <p className="text-center text-sky-700 text-sm">No subcategories found.</p>
        ) : null}
      </div>

      {/* Highlights + PowerPack */}
      <div className="mt-10 flex flex-col lg:flex-row gap-6">
        {/* Image carousel */}
        <div className="bg-white border border-sky-100 shadow-sm rounded-2xl overflow-hidden w-full lg:flex-1">
          <div className="px-4 py-3 border-b border-sky-100 flex items-center justify-between" />
          <div className="h-[220px] xs:h-[260px] sm:h-[320px] lg:h-[400px]">
            <Swiper
              modules={[Autoplay, Navigation]}
              autoplay={prefersReducedMotion ? false : { delay: 3000, disableOnInteraction: false }}
              loop
              navigation
              className="h-full w-full"
              aria-label="Highlights carousel"
            >
              {imageSlides.map((slide, index) => (
                <SwiperSlide key={index} className="bg-slate-100">
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    className="w-full h-full object-contain img-crisp select-none"
                    draggable="false"
                    loading={index === 0 ? "eager" : "lazy"}
                    fetchpriority={index === 0 ? "high" : "low"}
                    decoding="async"
                    width="1280"
                    height="720"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 66vw"
                    onError={(e) => {
                      e.currentTarget.src =
                        "data:image/svg+xml;charset=UTF-8," +
                        encodeURIComponent(
                          `<svg xmlns='http://www.w3.org/2000/svg' width='1280' height='720'><rect width='100%' height='100%' fill='#0b1220'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#94a3b8' font-family='Arial' font-size='16'>Image unavailable</text></svg>`
                        );
                    }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          <noscript>
            <div style={{ display: "grid", gap: 8, padding: 12 }}>
              {imageSlides.map((s, i) => (
                <img
                  key={i}
                  src={s.src}
                  alt={s.alt}
                  width="1280"
                  height="720"
                  style={{ width: "100%", height: "auto", objectFit: "contain", background: "#f1f5f9" }}
                />
              ))}
            </div>
          </noscript>
        </div>

        {/* PowerPack product slider */}
        <div className="bg-white border border-sky-100 shadow-sm rounded-2xl w-full lg:w-[360px] overflow-hidden">
          <div className="px-4 py-3 border-b border-sky-100 flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-sky-900">PowerPack Products</h3>
          </div>

          <div className="h-[320px] sm:h-[360px] lg:h-[400px]">
            <Swiper
              modules={[Autoplay, Navigation]}
              autoplay={prefersReducedMotion ? false : { delay: 6000, disableOnInteraction: false }}
              navigation
              slidesPerView={1}
              loop
              className="h-full"
              aria-label="PowerPack products carousel"
            >
              {productBox2.map((product) => (
                <SwiperSlide key={product._id} className="px-2 py-3">
                  <button
                    onClick={() => handleImageClickPowerPack(product._id)}
                    className="w-full h-full rounded-xl border border-sky-100 bg-white shadow-sm flex flex-col items-center justify-between text-center p-4 gap-2 transition hover:shadow-md hover:-translate-y-[1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sky-400"
                    aria-label={`View ${product.name}`}
                  >
                    <div className="relative w-40 h-40">
                      <img
                        src={`data:${product.image?.contentType};base64,${product.image?.data}`}
                        alt={product.name}
                        className="absolute inset-0 w-full h-full object-contain bg-slate-100 rounded-lg img-crisp"
                        loading="lazy"
                        decoding="async"
                        fetchpriority="low"
                        width="160"
                        height="160"
                        sizes="160px"
                        onError={(e) => {
                          e.currentTarget.src =
                            "data:image/svg+xml;charset=UTF-8," +
                            encodeURIComponent(
                              `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><rect width='100%' height='100%' fill='#e2e8f0'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#64748b' font-family='Arial' font-size='12'>Image unavailable</text></svg>`
                            );
                        }}
                      />
                    </div>
                    <div className="mt-1">
                      <h4 className="text-sm font-semibold text-sky-900 line-clamp-2 min-h-[2.25rem]">
                        {product.name}
                      </h4>
                      <p className="text-sm text-sky-700 mt-1">{formatINR(product.price)}</p>
                    </div>
                  </button>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        .img-crisp {
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
          backface-visibility: hidden;
          transform: translateZ(0);
        }

        .swiper-button-prev, .swiper-button-next { display: none; }
        @media (min-width: 768px) {
          .swiper-button-prev, .swiper-button-next { display: flex; color: #0f172a; }
          .swiper-button-prev:hover, .swiper-button-next:hover { color: #475569; }
        }
      `}</style>
    </section>
  );
};

export default Onexvideo;
